import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { BrightfieldPostIngestStep, MifPostIngestStep, Study } from 'interfaces/study';
import { includes } from 'lodash';
import React, { FunctionComponent } from 'react';

interface PostIngestPipelineSettingsProps {
  updatedSettings?: Study['settings'];
  setUpdatedSettings: React.Dispatch<React.SetStateAction<Study['settings']>>;
  editable?: boolean;
}

const NORMALIZATION: MifPostIngestStep = 'normalization';
const CELL_SEGMENTATION: MifPostIngestStep = 'cellSegmentation';
const BRIGHTFIELD_CELL_SEGMENTATION_ARIA_LABEL = 'Brightfield cell segmentation';
const MIF_NORMALIZATION_ARIA_LABEL = 'mIF normalization';
const MIF_CELL_SEGMENTATION_ARIA_LABEL = 'mIF cell segmentation';
const MIF_DNA_TILE_PERCENTILE_CAPS_ARIA_LABEL = 'mIF DNA tile percentile caps';

export const PostIngestPipelineSettings: FunctionComponent<PostIngestPipelineSettingsProps> = ({
  updatedSettings,
  setUpdatedSettings,
  editable = true,
}) => {
  const brightfieldSteps = updatedSettings?.postIngestPipeline?.brightfield?.additionalSteps ?? [];
  const mifSettings = updatedSettings?.postIngestPipeline?.mIF;
  const mifSteps = mifSettings?.additionalSteps ?? [];

  const updateBrightfieldSteps = (nextBrightfieldSteps: BrightfieldPostIngestStep[]) => {
    setUpdatedSettings((currentSettings) => ({
      ...currentSettings,
      postIngestPipeline: {
        brightfield: { additionalSteps: nextBrightfieldSteps },
        mIF: {
          additionalSteps: currentSettings?.postIngestPipeline?.mIF?.additionalSteps ?? [],
          dnaTilePercentileCapsEnabled: currentSettings?.postIngestPipeline?.mIF?.dnaTilePercentileCapsEnabled ?? false,
        },
      },
    }));
  };

  const updateMifSteps = (nextMifSteps: MifPostIngestStep[]) => {
    setUpdatedSettings((currentSettings) => ({
      ...currentSettings,
      postIngestPipeline: {
        brightfield: { additionalSteps: currentSettings?.postIngestPipeline?.brightfield?.additionalSteps ?? [] },
        mIF: {
          additionalSteps: nextMifSteps,
          dnaTilePercentileCapsEnabled: includes(nextMifSteps, NORMALIZATION)
            ? currentSettings?.postIngestPipeline?.mIF?.dnaTilePercentileCapsEnabled ?? false
            : false,
        },
      },
    }));
  };

  const updateDnaTilePercentileCaps = (enabled: boolean) => {
    setUpdatedSettings((currentSettings) => {
      const currentMifSteps = currentSettings?.postIngestPipeline?.mIF?.additionalSteps ?? [];
      return {
        ...currentSettings,
        postIngestPipeline: {
          brightfield: { additionalSteps: currentSettings?.postIngestPipeline?.brightfield?.additionalSteps ?? [] },
          mIF: {
            additionalSteps:
              enabled && !includes(currentMifSteps, NORMALIZATION)
                ? [NORMALIZATION, ...currentMifSteps]
                : currentMifSteps,
            dnaTilePercentileCapsEnabled: enabled,
          },
        },
      };
    });
  };

  const brightfieldCellSegmentationEnabled = includes(brightfieldSteps, CELL_SEGMENTATION);
  const mifNormalizationEnabled = includes(mifSteps, NORMALIZATION);
  const mifCellSegmentationEnabled = includes(mifSteps, CELL_SEGMENTATION);
  const dnaTilePercentileCapsEnabled = mifSettings?.dnaTilePercentileCapsEnabled ?? false;

  return (
    <Box data-cy="post-ingest-pipeline-settings" sx={{ minWidth: 0, maxWidth: '100%' }}>
      <Typography variant="h6">Initial image analysis</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every ingested slide is cloud optimized before tissue segmentation. mIF pipelines also create a histogram.
        Choose any additional processing to append to each pipeline.
      </Typography>

      <Typography variant="subtitle2">Brightfield</Typography>
      <Typography variant="body2" color="text.secondary">
        Always runs: Cloud optimization → Tissue segmentation
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={brightfieldCellSegmentationEnabled}
            disabled={!editable}
            inputProps={{ 'aria-label': BRIGHTFIELD_CELL_SEGMENTATION_ARIA_LABEL }}
            onChange={(_, checked) => updateBrightfieldSteps(checked ? [CELL_SEGMENTATION] : [])}
          />
        }
        label="Add cell segmentation"
      />

      <Typography variant="subtitle2" sx={{ mt: 2 }}>
        mIF
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Always runs: Cloud optimization → Tissue segmentation → Histogram creation
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <FormControlLabel
          control={
            <Switch
              checked={mifNormalizationEnabled}
              disabled={!editable}
              inputProps={{ 'aria-label': MIF_NORMALIZATION_ARIA_LABEL }}
              onChange={(_, checked) => updateMifSteps(checked ? [NORMALIZATION] : [])}
            />
          }
          label="Add normalization"
        />
        <Box
          role="group"
          aria-label="Multiplex normalization options"
          sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', pl: 4 }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={dnaTilePercentileCapsEnabled}
                disabled={!editable}
                inputProps={{ 'aria-label': MIF_DNA_TILE_PERCENTILE_CAPS_ARIA_LABEL }}
                onChange={(_, checked) => updateDnaTilePercentileCaps(checked)}
              />
            }
            label="Apply DNA tile percentile caps"
          />
          <Typography variant="caption" color="text.secondary" sx={{ pl: 1.5 }}>
            Applies percentile limits to DNA tiles during normalization.
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={mifCellSegmentationEnabled}
              disabled={!editable}
              inputProps={{ 'aria-label': MIF_CELL_SEGMENTATION_ARIA_LABEL }}
              onChange={(_, checked) => updateMifSteps(checked ? [NORMALIZATION, CELL_SEGMENTATION] : [NORMALIZATION])}
            />
          }
          label="Add cell segmentation"
        />
        <Typography variant="caption" color="text.secondary">
          Cell segmentation requires normalization. Adding it enables normalization.
        </Typography>
      </Box>
    </Box>
  );
};
